import { ValueOf } from '@approvalmax/types';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import {
    changeQbooksApplyTaxAfterDiscount,
    changeQbooksDiscount,
    changeQbooksDiscountType,
    changeQbooksTaxCode,
} from 'pages/requestForm/actions';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getQBooksContext } from 'pages/requestForm/selectors/qbooks';
import { getTaxCodeList } from 'pages/requestForm/selectors/qbooks/taxSelectors';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useCompanyBasedOnUrl } from 'shared/hooks';

import { basedOnLocationTax } from './QBooksTaxSwitcher.constants';

export const useQbooksInvoiceRequest = () => {
    return <domain.QBooksInvoiceRequest>useSelector(requestSelector);
};

export const useSwitcher = () => {
    const dispatch = useDispatch();

    const company = useCompanyBasedOnUrl();
    const request = useQbooksInvoiceRequest();
    const qBooksContext = useSelector(getQBooksContext);

    const { details } = request;

    const isUSCompany = company?.integration?.countryCode === backend.OrganisationVersion.QBO_US;
    const isInitialDiscountPresent = useRef(Boolean(details.discount));
    const isDiscountEnabled =
        qBooksContext.salesFormOptionalFields?.discountEnabled || isInitialDiscountPresent.current;

    const handleSwitch = useCallback(() => {
        dispatch(changeQbooksApplyTaxAfterDiscount(!details.applyTaxAfterDiscount));
    }, [dispatch, details.applyTaxAfterDiscount]);

    return {
        isUSCompany,
        isDiscountEnabled,
        handleSwitch,
    };
};

export const useDiscount = () => {
    const dispatch = useDispatch();

    const handleChangeDiscountType = useCallback(
        (type: ValueOf<domain.QBooksDiscountType>) => {
            dispatch(changeQbooksDiscountType(type));
        },
        [dispatch]
    );

    const handleChangeDiscount = useCallback(
        (value: number | null) => {
            dispatch(changeQbooksDiscount(value));
        },
        [dispatch]
    );

    return {
        handleChangeDiscountType,
        handleChangeDiscount,
    };
};

export const useTaxRates = () => {
    const dispatch = useDispatch();

    const request = useQbooksInvoiceRequest();
    const qBooksContext = useSelector(getQBooksContext);

    const { details } = request;

    const taxRates = useMemo(() => {
        const taxCodeList = getTaxCodeList(qBooksContext, details.date, domain.TaxApplicableOnType.Sales);

        return qBooksContext.isAutoTaxesEnabled ? [basedOnLocationTax, ...taxCodeList] : taxCodeList;
    }, [qBooksContext, details.date]);

    const handleChangeTaxRate = useCallback(
        (id: string | null) => {
            const taxCode = taxRates.find((rate) => rate.id === id) ?? null;

            dispatch(changeQbooksTaxCode(taxCode));
        },
        [dispatch, taxRates]
    );

    return {
        taxRates,
        handleChangeTaxRate,
    };
};
